/**
 * EmailPasswordForm — formulaire Login / Inscription email+mot de passe.
 *
 * Communique avec /api/auth/login, /api/auth/register, /api/auth/forgot-password.
 * Stocke le `session_token` reçu dans `localStorage` (clé `session_token`)
 * pour rester compatible avec le mécanisme Bearer token existant.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Loader2, Mail, Lock, User, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function EmailPasswordForm({ onSuccess, initialMode = "login" }) {
  const [mode, setMode] = useState(initialMode); // "login" | "register" | "forgot"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [unverifiedEmail, setUnverifiedEmail] = useState("");

  async function callApi(path, body) {
    const res = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = formatErrorDetail(data?.detail) || `HTTP ${res.status}`;
      throw new Error(msg);
    }
    return data;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);
    try {
      if (mode === "login") {
        const data = await callApi("/auth/login", { email, password });
        window.localStorage.setItem("session_token", data.session_token);
        toast.success(`Bienvenue, ${data.name} !`);
        if (onSuccess) onSuccess(data);
      } else if (mode === "register") {
        if (password.length < 8) throw new Error("Le mot de passe doit faire au moins 8 caractères.");
        const data = await callApi("/auth/register", { email, password, name });
        if (data.session_token) {
          window.localStorage.setItem("session_token", data.session_token);
          toast.success("Compte créé. Bienvenue sur Véyèr CIEL !");
          if (onSuccess) onSuccess(data);
        } else {
          toast.success(data.message || "Compte créé ! Vérifiez votre e-mail.");
          setInfo(data.message || `Un e-mail de confirmation a été envoyé à ${email}. Cliquez le lien pour activer votre compte.`);
          setMode("login");
        }
      } else if (mode === "forgot") {
        const data = await callApi("/auth/forgot-password", { email });
        toast.success(data.message || "Email envoyé.");
        setInfo(data.message || "Si un compte existe, un e-mail vient d'être envoyé.");
        setMode("login");
      }
    } catch (err) {
      setError(err.message);
      if (/non v[ée]rifi/i.test(err.message)) setUnverifiedEmail(email);
    } finally {
      setLoading(false);
    }
  }

  async function resendVerification() {
    try {
      const data = await callApi("/auth/resend-verification", { email: unverifiedEmail || email });
      toast.success(data.message || "E-mail de confirmation renvoyé.");
      setInfo(data.message || "E-mail de confirmation renvoyé.");
      setError("");
    } catch {
      toast.error("Impossible de renvoyer l'e-mail.");
    }
  }

  const isRegister = mode === "register";
  const isForgot = mode === "forgot";

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="email-auth-form">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold" style={{ color: "#1A1A1A", fontFamily: "Manrope, sans-serif" }}>
          {mode === "login" && "Connexion"}
          {mode === "register" && "Créer un compte"}
          {mode === "forgot" && "Mot de passe oublié"}
        </h3>
        {(isRegister || isForgot) && (
          <button
            type="button"
            onClick={() => { setMode("login"); setError(""); }}
            className="text-[11px] flex items-center gap-1 text-emerald-700 hover:underline"
            data-testid="back-to-login-btn"
          >
            <ArrowLeft className="w-3 h-3" /> Retour
          </button>
        )}
      </div>

      {/* Name (register only) */}
      {isRegister && (
        <div>
          <Label htmlFor="name" className="text-[11px] text-neutral-600">Votre nom</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
            <Input
              id="name"
              data-testid="auth-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              minLength={1}
              autoComplete="name"
              placeholder="Jean Dupont"
              className="h-10 pl-9 text-sm"
            />
          </div>
        </div>
      )}

      {/* Email */}
      <div>
        <Label htmlFor="email" className="text-[11px] text-neutral-600">Email</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
          <Input
            id="email"
            data-testid="auth-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            placeholder="vous@example.com"
            className="h-10 pl-9 text-sm"
          />
        </div>
      </div>

      {/* Password */}
      {!isForgot && (
        <div>
          <Label htmlFor="password" className="text-[11px] text-neutral-600">
            Mot de passe {isRegister && <span className="text-neutral-400">(min. 8 caractères)</span>}
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
            <PasswordInput
              id="password"
              data-testid="auth-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={isRegister ? 8 : 1}
              autoComplete={isRegister ? "new-password" : "current-password"}
              placeholder="••••••••"
              className="h-10 pl-9 text-sm"
            />
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <p data-testid="auth-error" className="text-xs text-red-600 bg-red-50 p-2 rounded">
          {error}
        </p>
      )}

      {/* Info (compte créé / vérification) */}
      {info && (
        <p data-testid="auth-info" className="text-xs text-emerald-700 bg-emerald-50 p-2 rounded">
          {info}
        </p>
      )}

      {/* Renvoyer la confirmation (e-mail non vérifié) */}
      {unverifiedEmail && (
        <button
          type="button"
          onClick={resendVerification}
          className="text-[11px] text-emerald-700 hover:underline"
          data-testid="resend-verification-btn"
        >
          Renvoyer l'e-mail de confirmation
        </button>
      )}

      {/* Submit */}
      <Button
        data-testid="auth-submit-btn"
        type="submit"
        disabled={loading}
        className="w-full h-10 rounded-full text-sm font-semibold"
        style={{ background: "#2D6A4F", color: "white", fontFamily: "Manrope, sans-serif" }}
      >
        {loading ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Patientez...</>
        ) : (
          mode === "login" ? "Se connecter" : isRegister ? "Créer mon compte" : "Envoyer le lien"
        )}
      </Button>

      {/* Switch links */}
      {mode === "login" && (
        <div className="flex items-center justify-between text-[11px]">
          <button
            type="button"
            onClick={() => { setMode("register"); setError(""); }}
            className="text-emerald-700 hover:underline"
            data-testid="switch-to-register-btn"
          >
            Pas encore de compte ? Créer
          </button>
          <button
            type="button"
            onClick={() => { setMode("forgot"); setError(""); }}
            className="text-neutral-500 hover:underline"
            data-testid="switch-to-forgot-btn"
          >
            Mot de passe oublié ?
          </button>
        </div>
      )}
    </form>
  );
}
